import {
  type CreatePortfolioInput,
  createPortfolioInputSchema,
  type PortfolioAdminDetail,
  type PortfolioMutationResult,
  type PortfolioSaveInput,
  portfolioCreateResolutionSchema,
  updatePortfolioInputSchema,
} from '@repo/api-contracts';
import { useRef, useState } from 'react';
import { useInvalidatePortfolios, usePortfolioDetailQuery } from '@/entities/portfolio';
import { trpc } from '@/shared/api';

interface SaveOptions {
  readonly id?: string;
  readonly expectedUpdatedAt?: string;
  readonly onSuccess: (result: PortfolioMutationResult) => void;
}

/** 실패한 등록 ID를 유지하고, 결과 불확실 시 정확한 요청만 다시 보낸다. */
export const useSavePortfolio = ({ id, expectedUpdatedAt, onSuccess }: SaveOptions) => {
  const [createId] = useState(() => crypto.randomUUID());
  const token = useRef<string | null>(null);
  const baseline = useRef(expectedUpdatedAt);
  const pending = useRef(false);
  const uncertainRequest = useRef<CreatePortfolioInput | null>(null);
  const [isPending, setPending] = useState(false);
  const [isUncertain, setUncertain] = useState(false);
  const [isConflict, setConflict] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [latest, setLatest] = useState<PortfolioAdminDetail | null>(null);
  const create = trpc.portfolioManagement.items.create.useMutation();
  const update = trpc.portfolioManagement.items.update.useMutation();
  const detail = usePortfolioDetailQuery(id ?? createId, false);
  const invalidate = useInvalidatePortfolios();

  const save = async (input: PortfolioSaveInput): Promise<void> => {
    if (pending.current) return;
    const wasUncertain = uncertainRequest.current !== null;
    const parsed = id
      ? updatePortfolioInputSchema.safeParse({ ...input, id, expectedUpdatedAt: baseline.current })
      : createPortfolioInputSchema.safeParse(
          uncertainRequest.current ?? { ...input, id: createId, createToken: token.current },
        );
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? '입력값을 확인해주세요.');
      return;
    }
    pending.current = true;
    setPending(true);
    setError(null);
    try {
      const result = id
        ? await update.mutateAsync(updatePortfolioInputSchema.parse(parsed.data))
        : await create.mutateAsync(createPortfolioInputSchema.parse(parsed.data));
      uncertainRequest.current = null;
      setUncertain(false);
      setConflict(false);
      invalidate();
      onSuccess(result);
    } catch (cause) {
      const code =
        typeof cause === 'object' && cause !== null && 'data' in cause
          ? (cause as { data?: { code?: string } }).data?.code
          : undefined;
      const message =
        typeof cause === 'object' && cause !== null && 'message' in cause
          ? String(cause.message)
          : '저장하지 못했습니다. 다시 시도해주세요.';
      setError(message);
      setConflict(Boolean(id && code === 'CONFLICT'));
      // 이미 불확실한 결과는 인증 거절·충돌·GET NOT_FOUND로 rollback을 증명할 수 없다.
      const definiteInitialRejection = [
        'BAD_REQUEST',
        'UNAUTHORIZED',
        'FORBIDDEN',
        'NOT_FOUND',
        'CONFLICT',
      ].includes(code ?? '');
      if (!id && (wasUncertain || !definiteInitialRejection)) {
        uncertainRequest.current = createPortfolioInputSchema.parse(parsed.data);
        setUncertain(true);
      }
    } finally {
      pending.current = false;
      setPending(false);
    }
  };

  const recover = async (): Promise<void> => {
    if (pending.current) return;
    pending.current = true;
    setPending(true);
    setError(null);
    try {
      if (uncertainRequest.current) {
        const resolution = portfolioCreateResolutionSchema.parse(
          await detail.resolveCreate(uncertainRequest.current.createToken),
        );
        if (resolution.status === 'RETRYABLE') {
          uncertainRequest.current = null;
          setUncertain(false);
          setLatest(null);
          return;
        }
      }
      setLatest(await detail.fetchLatest());
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : '최신 결과를 확인하지 못했습니다. 같은 요청으로 재시도해주세요.',
      );
      return;
    } finally {
      pending.current = false;
      setPending(false);
    }
    if (uncertainRequest.current) await save(uncertainRequest.current);
  };

  return {
    createId,
    setCreateToken: (value: string) => {
      token.current ??= value;
    },
    save,
    recover,
    isPending,
    isUncertain,
    isConflict,
    error,
    latest,
    adoptBaseline: (value: string) => {
      if (pending.current || uncertainRequest.current) return;
      baseline.current = value;
      setConflict(false);
      setError(null);
      setLatest(null);
    },
  };
};
